import { Injectable } from '@angular/core';

import { BehaviorSubject } from 'rxjs';

import { EMicStatus, EMicError } from 'app/enums/voice-call.enums';

@Injectable({ providedIn: 'root' })
export class MicrophoneService {
  private readonly _status$: BehaviorSubject<EMicStatus> = new BehaviorSubject<EMicStatus>(
    EMicStatus.UNKNOWN
  );
  public readonly status$ = this._status$.asObservable();

  MIC_LS_KEY: string = 'app_mic_permission_status';
  MIC_DENIED_FLAG_KEY: string = 'app_mic_was_denied';

  // ---- LocalStorage helpers ----
  private safeSetItem(key: string, value: string): void {
    localStorage.setItem(key, value);
  }

  private safeGetItem(key: string): string | null {
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  }

  private safeRemoveItem(key: string): void {
    localStorage.removeItem(key);
  }

  // ---- Core state handling ----
  private updateStatus(status: EMicStatus): void {
    this.safeSetItem(this.MIC_LS_KEY, status);
    this._status$.next(status);
  }

  private async queryPermission(): Promise<EMicStatus> {
    if (!navigator.permissions?.query) return EMicStatus.UNKNOWN;

    try {
      const p: PermissionStatus = await navigator.permissions.query({
        name: 'microphone' as PermissionName
      });
      return p.state as EMicStatus;
    } catch {
      return EMicStatus.UNKNOWN;
    }
  }

  // ---- Initialization ----
  public async init(): Promise<void> {
    const persisted = this.safeGetItem(this.MIC_LS_KEY) as EMicStatus | null;

    if (!persisted) {
      const status = await this.queryPermission();
      this.updateStatus(status);
      return;
    }

    if (persisted !== EMicStatus.GRANTED) {
      this._status$.next(persisted);
      return;
    }

    const status = await this.queryPermission();
    // Only update if the status has actually changed
    if (status !== this._status$.getValue()) {
      this.updateStatus(status);
    }
  }

  // ---- Request microphone access ----
  public async requestAccess(): Promise<MediaStream> {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // Only update status if it's not already granted
      if (this._status$.getValue() !== EMicStatus.GRANTED) {
        this.updateStatus(EMicStatus.GRANTED);
      }
      this.safeRemoveItem(this.MIC_DENIED_FLAG_KEY);
      return stream;
    } catch (err: unknown) {
      this.handleRequestError(err);
    }
  }

  private handleRequestError(err: unknown): never {
    const error = err as DOMException | Error;
    const name = error.name || error.message || '';

    const permissionErrors = ['NotAllowedError', 'SecurityError', 'PermissionDeniedError'];
    const deviceErrors = ['NotFoundError', 'DevicesNotFoundError', 'OverconstrainedError'];

    if (permissionErrors.includes(name)) {
      this.updateStatus(EMicStatus.DENIED);
      this.safeSetItem(this.MIC_DENIED_FLAG_KEY, '1');
      throw new Error(EMicError.PERMISSION_DENIED);
    }

    if (deviceErrors.includes(name)) {
      this.updateStatus(EMicStatus.NO_DEVICE);
      throw new Error(EMicError.NO_DEVICE);
    }

    this.updateStatus(EMicStatus.ERROR);
    throw new Error(EMicError.GENERIC);
  }

  // ---- Utilities ----
  public getLastStatus(): EMicStatus {
    return this._status$.getValue();
  }

  public resetStored(): void {
    this.safeRemoveItem(this.MIC_LS_KEY);
    this.safeRemoveItem(this.MIC_DENIED_FLAG_KEY);
    this._status$.next(EMicStatus.UNKNOWN);
  }
}
